/**
 * Header group behavior.
 *
 * Everything the header/announcement bar need to *work* (nav dropdowns,
 * mobile drawer, mobile search) is plain <details>/<summary> and needs no
 * JS — including the desktop nav's shared expansion panel, which has a
 * pure-CSS :has()-based fallback (see the <nav-expansion> notes below and
 * the inline styles generated in sections/header.liquid). This file adds
 * progressive enhancements on top:
 *
 * - <sticky-header>: shows/hides the header on scroll direction when
 *   data-sticky-type="on-scroll-up", and toggles a .scrolled-past class used
 *   for the header's drop shadow.
 * - <announcement-bar>: prev/next controls and hover-to-pause for the CSS
 *   rotation when there is more than one block.
 * - <nav-expansion>: Atelier-style desktop nav — click pins a shared
 *   full-width panel open, hovering previews it after a short intent delay,
 *   and outside click / Esc / focus leaving the header closes it. It works
 *   by managing the real [open] attribute on the active <details> (the same
 *   attribute the no-JS CSS fallback reacts to) plus the `hidden` attribute
 *   on the matching content block and an .is-open class on the panel, so
 *   the experience doesn't depend on :has() browser support. Within an open
 *   panel, hovering/focusing a left-hand link also swaps which right-zone
 *   product-preview set is visible (bindPreviewSwap) — all sets are
 *   rendered server-side and toggled via `hidden`, so the swap is instant.
 * - <focus-disclosure>: wraps the mobile nav drawer, a plain <details>/
 *   <summary> so the no-JS case works. Adds the dialog-like behavior a
 *   keyboard/screen-reader user expects once JS is available: focus moves
 *   into the panel on open and back to the trigger on close, Esc closes,
 *   and (since its scrim blocks the rest of the page) Tab is trapped inside
 *   the open panel and a scrim click closes it. A wrapper element (not a
 *   customized-built-in `is=` on <details> itself) so it works in Safari
 *   too; `display: contents` in section-header.css keeps it out of the
 *   surrounding flex/grid layout.
 * - <search-overlay>: the header search icon's <details> panel, wrapped the
 *   same way as <focus-disclosure> (open/focus/Esc/trap/scrim-close/return
 *   focus) but with its own element because the overlay also needs a body
 *   scroll lock and to focus the search input specifically on open, not
 *   just the first focusable element. Live predictive results are a
 *   separate concern, handled by <predictive-search> (assets/
 *   predictive-search.js) nested inside the panel.
 */

if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastScrollY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      // Publishes the pinned header's height as --header-sticky-height, so
      // anything else that sticks (the product gallery) can sit just under
      // it instead of sliding beneath it. 0 when the header does not stick.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          const sticks = this.dataset.stickyType === 'always' || this.dataset.stickyType === 'on-scroll-up';
          document.documentElement.style.setProperty(
            '--header-sticky-height',
            (sticks ? Math.round(this.getBoundingClientRect().height) : 0) + 'px'
          );
        });
        this.resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    onScroll() {
      const scrollY = window.scrollY;
      const stickyType = this.dataset.stickyType;

      this.classList.toggle('scrolled-past', scrollY > 4);

      if (stickyType === 'on-scroll-up') {
        const scrolledDown = scrollY > this.lastScrollY;
        const pastHeader = scrollY > this.offsetHeight;
        this.classList.toggle('header-wrapper--hidden', scrolledDown && pastHeader);
      }

      this.lastScrollY = scrollY;
    }
  }

  customElements.define('sticky-header', StickyHeader);
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement-bar__item'));
      this.activeIndex = 0;

      if (this.items.length <= 1) return;

      this.prevButton = this.querySelector('[data-announcement-prev]');
      this.nextButton = this.querySelector('[data-announcement-next]');

      this.onPrev = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onEnter = () => this.setAttribute('data-paused', 'true');
      this.onLeave = () => this.removeAttribute('data-paused');

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
    }

    disconnectedCallback() {
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
    }

    step(direction) {
      this.setAttribute('data-manual', 'true');
      this.items[this.activeIndex].classList.remove('is-active');
      this.activeIndex = (this.activeIndex + direction + this.items.length) % this.items.length;
      this.items[this.activeIndex].classList.add('is-active');
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}

if (!customElements.get('nav-expansion')) {
  const HOVER_INTENT_DELAY = 120;
  // Grace period before a hover-opened panel closes. Without it, any brief
  // trip outside the subtree - most obviously the header's own bottom
  // padding, which sits between the nav row and the panel and belongs to
  // neither - closes the panel before the pointer can reach it.
  const HOVER_CLOSE_DELAY = 220;

  class NavExpansion extends HTMLElement {
    connectedCallback() {
      this.detailsList = Array.from(this.querySelectorAll(':scope > nav > ul > li > details.header__menu-details'));
      this.panel = this.querySelector(':scope > .header__expansion-panel');

      if (!this.detailsList.length || !this.panel) return;

      this.activeDetails = null;
      this.pinned = false;
      this.hoverTimer = null;

      this.detailsList.forEach((details) => {
        const summary = details.querySelector(':scope > summary');
        if (!summary) return;

        // <details> already toggles [open] natively on click; we only need
        // to intercept to (a) keep exclusivity in sync with our own state
        // (name= grouping already does this at the DOM level, this mirrors
        // it in JS-land) and (b) track "pinned" so a later plain hover on a
        // *different* item can still preview over a pinned one, per spec.
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          clearTimeout(this.hoverTimer);
          const reopening = this.activeDetails === details && this.pinned;
          this.closeAll();
          if (!reopening) this.open(details, true);
        });

        details.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.hoverTimer);
          if (this.activeDetails === details) return;
          this.hoverTimer = setTimeout(() => this.open(details, false), HOVER_INTENT_DELAY);
        });
      });

      // Close-on-leave is bound to the whole element (trigger row + panel),
      // not each <details> — the panel is a DOM descendant of <nav-expansion>
      // even though it's visually full-width, so moving the pointer from a
      // summary down into the panel below never fires this (the pointer
      // never actually leaves the subtree); only actually mousing away from
      // both areas does.
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.hoverTimer);
        if (this.pinned) return;
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.closeAll(), HOVER_CLOSE_DELAY);
      });

      // Coming back inside during the grace period cancels the pending
      // close, so travelling from a nav item down into the panel reads as
      // one continuous gesture.
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.closeTimer);
      });

      this.onDocumentPointerDown = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.activeDetails) return;
        const trigger = this.activeDetails.querySelector(':scope > summary');
        this.closeAll();
        if (trigger) trigger.focus();
      };
      this.onFocusOut = () => {
        // Defer so document.activeElement reflects where focus is landing.
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.closeAll();
        });
      };

      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusOut);

      this.bindPreviewSwap();
    }

    // Right-zone product previews: every left-hand link with a
    // [data-preview-target] has a matching [data-preview-set] rendered
    // server-side (see header.liquid) inside its panel's
    // [data-preview-group]. Hovering or focusing the link just toggles
    // `hidden` on the sets — nothing is fetched, so the swap is instant.
    // pointerenter/focusin don't bubble, so listeners are bound per link
    // rather than delegated from the panel.
    bindPreviewSwap() {
      this.panel.querySelectorAll('[data-preview-target]').forEach((link) => {
        const content = link.closest('[data-expansion-content]');
        const group = content?.querySelector('[data-preview-group]');
        if (!group) return;

        const showPreview = () => {
          const target = link.dataset.previewTarget;
          group.querySelectorAll('[data-preview-set]').forEach((set) => {
            set.hidden = set.dataset.previewSet !== target;
          });
          content
            .querySelectorAll('[data-preview-target]')
            .forEach((otherLink) => otherLink.classList.toggle('is-previewing', otherLink === link));

          // "View all X" in the right zone's top-right corner tracks
          // whichever set just became visible — each set carries its own
          // link's URL/localized label as data attributes (see
          // header.liquid), so this is a plain read + write, no re-fetch.
          const viewAll = content.querySelector('[data-view-all]');
          const activeSet = group.querySelector(`[data-preview-set="${target}"]`);
          if (viewAll && activeSet && activeSet.dataset.viewAllUrl) {
            viewAll.href = activeSet.dataset.viewAllUrl;
            const label = viewAll.querySelector('[data-view-all-label]');
            if (label) label.textContent = activeSet.dataset.viewAllLabel;
          }
        };

        link.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          showPreview();
        });
        link.addEventListener('focusin', showPreview);
      });
    }

    disconnectedCallback() {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.closeTimer);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusOut);
    }

    open(details, pinned) {
      if (this.activeDetails !== details) this.closeAll();

      details.open = true;
      const summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', 'true');

      const index = details.dataset.menuIndex;
      const content = this.panel.querySelector(`[data-expansion-content="${index}"]`);
      if (content) {
        // Build this panel's product previews on first open (see the
        // <template> note in header.liquid).
        content.querySelectorAll('template[data-preview-template]').forEach((template) => {
          template.replaceWith(template.content);
        });
        content.hidden = false;
        // The tile images start loading="lazy" so a page load that never
        // opens the nav never fetches them — but native lazy-loading is a
        // viewport-intersection heuristic, and this content starts inside
        // both a [hidden] ancestor and a panel animating grid-template-rows
        // from 0fr, so some images get their first (and only) intersection
        // check while still zero-area and never get re-queued once the
        // panel finishes opening. Forcing eager here is a one-time no-op on
        // repeat opens (the attribute's already 'eager' after the first)
        // and guarantees every tile actually paints instead of racing the
        // lazy loader against the reveal animation.
        content.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      }

      this.panel.classList.add('is-open');
      this.activeDetails = details;
      this.pinned = pinned;
    }

    closeAll() {
      this.detailsList.forEach((details) => {
        details.open = false;
        const summary = details.querySelector(':scope > summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      });
      this.panel.querySelectorAll('[data-expansion-content]').forEach((content) => {
        content.hidden = true;
      });
      this.panel.classList.remove('is-open');
      this.activeDetails = null;
      this.pinned = false;
    }
  }

  customElements.define('nav-expansion', NavExpansion);
}

/**
 * Animated close for the two <details>-based overlays (the mobile nav
 * drawer and the search overlay), so they leave the way the four <dialog>
 * overlays do rather than snapping out.
 *
 * A <details> display:none's its own content the moment `open` is removed,
 * which kills any exit transition before it starts. So the details is kept
 * open, `.is-closing` is added to reverse the panel/scrim to their entry
 * "from" state, and `open = false` only lands once that transition ends
 * (or a guard timer fires - --motion-overlay is 320ms, plus a buffer).
 *
 * Reduced motion, or a browser with no matching transition, short-circuits
 * to the old instant close.
 */
function closeDisclosureWithMotion(details, panel, onClosed) {
  if (!details || !details.open) return;
  if (details.__closing) return;

  const done = () => {
    if (!details.__closing) return;
    clearTimeout(details.__closeTimer);
    details.__closing = false;
    if (panel) panel.removeEventListener('transitionend', onEnd);
    details.classList.remove('is-closing');
    details.open = false;
    if (onClosed) onClosed();
  };
  const onEnd = (event) => {
    if (event.target !== panel) return;
    done();
  };

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !panel) {
    details.classList.remove('is-closing');
    details.open = false;
    if (onClosed) onClosed();
    return;
  }

  details.__closing = true;
  panel.addEventListener('transitionend', onEnd);
  details.__closeTimer = setTimeout(done, 400);
  details.classList.add('is-closing');
}

// Cancels an in-flight animated close - used when the disclosure is
// reopened mid-exit, so it doesn't finish closing on top of the new open.
function cancelDisclosureClose(details) {
  if (!details || !details.__closing) return;
  clearTimeout(details.__closeTimer);
  details.__closing = false;
  details.classList.remove('is-closing');
}

/**
 * Both disclosures below trap Tab and both need "what can I focus in here",
 * and so do the two <dialog> overlays in quick-view.js/perfect-match.js -
 * four copies of the same block, with the selector string written out three
 * times. It now lives once in assets/overlay-motion.js, which is loaded
 * `defer` in the document head and therefore always executed before this
 * file (deferred scripts run in document order). These two wrappers exist
 * only so the null-guard is stated once rather than at every call site.
 */
function getFocusableElements(root) {
  return window.ThemeOverlay ? window.ThemeOverlay.getFocusable(root) : [];
}

function trapTab(event, root) {
  if (window.ThemeOverlay) window.ThemeOverlay.trapTab(event, root);
}

if (!customElements.get('focus-disclosure')) {
  class FocusDisclosure extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector(':scope > details');
      if (!this.details) return;

      this.summary = this.details.querySelector(':scope > summary');
      this.scrimEl = this.dataset.scrim ? this.details.querySelector(this.dataset.scrim) : null;
      // The summary IS the close control once the disclosure is open (its
      // icon swaps burger->close), so its accessible name has to swap too.
      // The closed-state name is whatever the markup shipped, which is also
      // what a no-JS visitor keeps.
      this.openLabel = this.summary ? this.summary.dataset.openLabel || null : null;
      this.closedLabel = this.summary ? this.summary.getAttribute('aria-label') : null;
      this.focusSelector = this.dataset.focusSelector || null;
      this.panelEl = this.dataset.panel ? this.details.querySelector(this.dataset.panel) : null;
      this.trap = this.dataset.trap === 'true';

      this.close = this.close.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSummaryClick = this.onSummaryClick.bind(this);

      if (this.scrimEl) this.scrimEl.addEventListener('click', this.close);
      if (this.summary) this.summary.addEventListener('click', this.onSummaryClick);
      this.details.addEventListener('toggle', this.onToggle);
      this.details.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.scrimEl) this.scrimEl.removeEventListener('click', this.close);
      if (this.summary) this.summary.removeEventListener('click', this.onSummaryClick);
      if (this.details) {
        this.details.removeEventListener('toggle', this.onToggle);
        this.details.removeEventListener('keydown', this.onKeydown);
        cancelDisclosureClose(this.details);
      }
    }

    // The summary is also the CLOSE control once open (its icon swaps
    // burger->close). Its native toggle would set open = false on the same
    // frame, skipping the exit animation every other close path now gets,
    // so that one direction is intercepted. Enter/Space on the summary
    // dispatch a click too, so this covers the keyboard as well. Without
    // JS this listener never exists and the native toggle stands.
    onSummaryClick(event) {
      if (!this.details.open) return;
      event.preventDefault();
      this.close();
    }

    close() {
      // Focus moves first, not in the completion callback: leaving the
      // caret inside a panel that is on its way out is worse than moving
      // it a frame early.
      if (this.summary) this.summary.focus();
      closeDisclosureWithMotion(this.details, this.panelEl);
    }

    onToggle() {
      if (this.details.open) cancelDisclosureClose(this.details);
      if (this.openLabel && this.summary) {
        this.summary.setAttribute('aria-label', this.details.open ? this.openLabel : this.closedLabel);
      }
      if (!this.details.open) return;
      const focusTarget =
        (this.focusSelector && this.details.querySelector(this.focusSelector)) ||
        getFocusableElements(this.details)[0];
      if (focusTarget) focusTarget.focus();
    }

    onKeydown(event) {
      if (!this.details.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (this.trap) trapTab(event, this.details);
    }
  }

  customElements.define('focus-disclosure', FocusDisclosure);
}

if (!customElements.get('search-overlay')) {
  class SearchOverlay extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector(':scope > details');
      if (!this.details) return;

      this.summary = this.details.querySelector(':scope > summary');
      this.panel = this.details.querySelector('.search-overlay__panel');
      this.scrim = this.details.querySelector('[data-search-scrim]');
      this.closeButton = this.details.querySelector('[data-search-close]');
      this.input = this.details.querySelector('[data-predictive-search-input]');

      this.close = this.close.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSummaryClick = this.onSummaryClick.bind(this);

      if (this.scrim) this.scrim.addEventListener('click', this.close);
      if (this.closeButton) this.closeButton.addEventListener('click', this.close);
      if (this.summary) this.summary.addEventListener('click', this.onSummaryClick);
      this.details.addEventListener('toggle', this.onToggle);
      this.details.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (this.scrim) this.scrim.removeEventListener('click', this.close);
      if (this.closeButton) this.closeButton.removeEventListener('click', this.close);
      if (this.summary) this.summary.removeEventListener('click', this.onSummaryClick);
      if (this.details) {
        this.details.removeEventListener('toggle', this.onToggle);
        this.details.removeEventListener('keydown', this.onKeydown);
        cancelDisclosureClose(this.details);
      }
      if (window.ThemeOverlay) window.ThemeOverlay.unlockScroll(this);
    }

    // Re-clicking the search toggle while the overlay is open would
    // natively snap it shut - route it through close() so it animates out
    // like every other close path. See FocusDisclosure#onSummaryClick.
    onSummaryClick(event) {
      if (!this.details.open) return;
      event.preventDefault();
      this.close();
    }

    close() {
      if (this.summary) this.summary.focus();
      closeDisclosureWithMotion(this.details, this.panel);
    }

    onToggle() {
      if (this.details.open) cancelDisclosureClose(this.details);
      // Shared with the four <dialog> overlays. `toggle` can fire more than
      // once for a single open (the animated close sets open = false only
      // after the exit transition), which is why lockScroll tracks owners
      // rather than counting calls - see ThemeOverlay.lockScroll.
      if (window.ThemeOverlay) {
        if (this.details.open) window.ThemeOverlay.lockScroll(this);
        else window.ThemeOverlay.unlockScroll(this);
      }
      if (!this.details.open) return;
      if (this.input) this.input.focus();
    }

    onKeydown(event) {
      if (!this.details.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      trapTab(event, this.panel || this.details);
    }
  }

  customElements.define('search-overlay', SearchOverlay);
}
